// ── Adapter Supabase do IGoalsRepository ──────────────────────────
// Implementa a porta do módulo Metas usando o cliente Supabase. É a ÚNICA
// camada que conhece a tabela metas e os joins de atingimento
// (leads/commissions/cards). period_key = 'YYYY-MM'.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import { getPeriodRange, shiftPeriod } from '@utils';
import type { GoalRow, GoalAchievementRow } from '@/types/goals';
import type {
  IGoalsRepository,
  ListGoalsParams,
  UpsertGoalParams,
  CopyFromPreviousMonthParams,
  GetGoalsAchievementParams,
} from '@modules/goals';

const requireSupabase = (): TypedSupabaseClient => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase não inicializado.');
  return s;
};

const SELECT_RICH = `
  *,
  user:profiles!metas_target_user_id_fkey ( id, nome, initials, color, avatar_url )
`;

export class SupabaseGoalsRepository implements IGoalsRepository {
  // ─── Listagens ──────────────────────────────────────────────────
  listGoals = async ({
    workspaceId,
    period,
    scope = null,
    funnelId,
  }: ListGoalsParams): Promise<GoalRow[]> => {
    if (!workspaceId) return [];
    const supabase = requireSupabase();

    let q = supabase
      .from('metas')
      .select(SELECT_RICH)
      .eq('workspace_id', workspaceId)
      .order('target_label', { ascending: true, nullsFirst: false });

    if (period) q = q.eq('period_key', period);
    if (scope)  q = q.eq('scope', scope);

    if (funnelId === null) {
      q = q.is('funnel_id', null);
    } else if (funnelId) {
      q = q.or(`funnel_id.is.null,funnel_id.eq.${funnelId}`);
    }

    const { data, error } = await q;
    if (error) throw error;
    return (data || []) as unknown as GoalRow[];
  };

  // ─── Mutations ──────────────────────────────────────────────────
  upsertGoal = async ({
    workspaceId, scope, periodKey,
    targetUserId = null, targetLabel = null,
    funnelId = null,
    valor, createdBy = null,
  }: UpsertGoalParams): Promise<GoalRow> => {
    const supabase = requireSupabase();

    // Tenta achar existente — chave inclui funnel_id.
    let q = supabase
      .from('metas')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('scope', scope)
      .eq('period_key', periodKey);

    if (targetUserId) q = q.eq('target_user_id', targetUserId);
    else              q = q.is('target_user_id', null);

    if (targetLabel)  q = q.eq('target_label', targetLabel);
    else              q = q.is('target_label', null);

    if (funnelId)     q = q.eq('funnel_id', funnelId);
    else              q = q.is('funnel_id', null);

    const { data: existing, error: findErr } = await q.maybeSingle();
    if (findErr && findErr.code !== 'PGRST116') throw findErr;

    if (existing?.id) {
      const { data, error } = await supabase
        .from('metas')
        .update({ valor: Number(valor), updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select(SELECT_RICH)
        .single();
      if (error) throw error;
      return data as unknown as GoalRow;
    }

    const { data, error } = await supabase
      .from('metas')
      .insert({
        workspace_id: workspaceId,
        scope,
        period_key: periodKey,
        target_user_id: targetUserId,
        target_label:   targetLabel,
        funnel_id:      funnelId || null,
        valor: Number(valor),
        created_by: createdBy,
      })
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return data as unknown as GoalRow;
  };

  deleteGoal = async (id: string): Promise<void> => {
    const supabase = requireSupabase();
    const { error } = await supabase.from('metas').delete().eq('id', id);
    if (error) throw error;
  };

  // Copia metas do mês anterior para o mês atual (sem sobrescrever existentes).
  copyFromPreviousMonth = async ({
    workspaceId,
    scope,
    periodKey,
    createdBy = null,
  }: CopyFromPreviousMonthParams): Promise<{ copied: number; skipped: number }> => {
    const supabase = requireSupabase();

    const previousKey = shiftPeriod(periodKey, -1);

    const { data: prevMetas, error: errPrev } = await supabase
      .from('metas')
      .select('scope, target_user_id, target_label, funnel_id, valor')
      .eq('workspace_id', workspaceId)
      .eq('scope', scope)
      .eq('period_key', previousKey);
    if (errPrev) throw errPrev;
    if (!prevMetas?.length) return { copied: 0, skipped: 0 };

    const { data: currentMetas, error: errCurr } = await supabase
      .from('metas')
      .select('target_user_id, target_label, funnel_id')
      .eq('workspace_id', workspaceId)
      .eq('scope', scope)
      .eq('period_key', periodKey);
    if (errCurr) throw errCurr;

    // Config/51: chave inclui funnel_id pra não colidir entre funis.
    const keyOf = (m: { target_user_id: string | null; target_label: string | null; funnel_id: string | null }): string =>
      `${m.target_user_id || ''}::${m.target_label || ''}::${m.funnel_id || ''}`;
    const existingKeys = new Set((currentMetas || []).map(keyOf));

    const toInsert = prevMetas
      .filter(m => !existingKeys.has(keyOf(m)))
      .map(m => ({
        workspace_id: workspaceId,
        scope,
        period_key: periodKey,
        target_user_id: m.target_user_id,
        target_label:   m.target_label,
        funnel_id:      m.funnel_id || null,
        valor: m.valor,
        created_by: createdBy,
      }));

    if (toInsert.length === 0) return { copied: 0, skipped: prevMetas.length };

    const { error: errIns } = await supabase.from('metas').insert(toInsert);
    if (errIns) throw errIns;

    return { copied: toInsert.length, skipped: prevMetas.length - toInsert.length };
  };

  // ─── Atingimento (joins com leads/commissions) ──────────────────
  getGoalsAchievement = async ({
    workspaceId,
    period,
    scope,
    funnelId,
  }: GetGoalsAchievementParams): Promise<GoalAchievementRow[]> => {
    if (!workspaceId || !period || !scope) return [];
    const supabase = requireSupabase();
    const range = getPeriodRange(period);
    if (!range) return [];

    const metas = await this.listGoals({ workspaceId, period, scope, funnelId });

    // Quando filtrado por funil, precisa do conjunto de lead_ids ligados a
    // cards de pipelines daquele funnel_id.
    let funnelLeadIds: Set<string> | null = null;
    if (funnelId) {
      const { data: scopedCards } = await supabase
        .from('cards')
        .select('lead_id, pipelines!inner(funnel_id)')
        .eq('pipelines.funnel_id', funnelId);
      funnelLeadIds = new Set(
        ((scopedCards || []) as unknown as Array<{ lead_id: string | null }>)
          .map(c => c.lead_id)
          .filter((id): id is string => !!id),
      );
    }
    const passesFunnel = (leadId: string | null): boolean =>
      funnelLeadIds == null || (leadId != null && funnelLeadIds.has(leadId));

    const mergeLabelMetasWithCounts = (counts: Record<string, number>): GoalAchievementRow[] => {
      const rows: GoalAchievementRow[] = metas.map(m => {
        const label = String(m.target_label || '').trim();
        const real = counts[label] || 0;
        const meta = Number(m.valor) || 0;
        return {
          meta_id:   m.id,
          key:       label,
          label,
          funnel_id: m.funnel_id || null,
          meta,
          real,
          percent:   meta > 0 ? (real / meta) : 0,
        };
      });
      const known = new Set(rows.map(row => row.key));
      Object.entries(counts).forEach(([key, real]) => {
        if (known.has(key)) return;
        rows.push({
          meta_id: null,
          key,
          label: key,
          meta: 0,
          real,
          percent: 0,
        });
      });
      return rows;
    };

    if (scope === 'leads_origem') {
      const { data: leads, error } = await supabase
        .from('leads')
        .select('id, origem')
        .eq('workspace_id', workspaceId)
        .eq('ativo', true)
        .gte('created_at', range.start_iso)
        .lt('created_at', range.end_iso);
      if (error) throw error;

      const counts: Record<string, number> = {};
      for (const l of (leads || [])) {
        if (!passesFunnel(l.id)) continue;
        const key = String(l.origem || '').trim() || '(sem origem)';
        counts[key] = (counts[key] || 0) + 1;
      }
      return mergeLabelMetasWithCounts(counts);
    }

    if (scope === 'leads_canal') {
      const { data: leads, error } = await supabase
        .from('leads')
        .select('id, canal')
        .eq('workspace_id', workspaceId)
        .eq('ativo', true)
        .gte('created_at', range.start_iso)
        .lt('created_at', range.end_iso);
      if (error) throw error;

      const counts: Record<string, number> = {};
      for (const l of (leads || [])) {
        if (!passesFunnel(l.id)) continue;
        const key = String(l.canal || '').trim() || '(sem canal)';
        counts[key] = (counts[key] || 0) + 1;
      }
      return mergeLabelMetasWithCounts(counts);
    }

    if (scope === 'receita_user') {
      const { data: comms, error } = await supabase
        .from('commissions')
        .select('closer_id, sdr_id, faturamento, lead_id')
        .eq('workspace_id', workspaceId)
        .gte('won_date', range.start_date)
        .lte('won_date', range.end_date);
      if (error) throw error;

      const sums: Record<string, number> = {};
      for (const c of (comms || [])) {
        if (!passesFunnel(c.lead_id)) continue;
        if (c.closer_id) sums[c.closer_id] = (sums[c.closer_id] || 0) + (Number(c.faturamento) || 0);
        if (c.sdr_id && c.sdr_id !== c.closer_id) {
          sums[c.sdr_id] = (sums[c.sdr_id] || 0) + (Number(c.faturamento) || 0);
        }
      }
      return metas.map(m => {
        const real = sums[m.target_user_id ?? ''] || 0;
        const meta = Number(m.valor) || 0;
        return {
          meta_id:   m.id,
          key:       m.target_user_id,
          label:     m.user?.nome || '(usuário)',
          user:      m.user,
          funnel_id: m.funnel_id || null,
          meta,
          real,
          percent:   meta > 0 ? (real / meta) : 0,
        };
      });
    }

    if (scope === 'receita_canal') {
      const { data: comms, error } = await supabase
        .from('commissions')
        .select('faturamento, lead_id, lead:leads ( canal )')
        .eq('workspace_id', workspaceId)
        .gte('won_date', range.start_date)
        .lte('won_date', range.end_date);
      if (error) throw error;

      const sums: Record<string, number> = {};
      for (const c of ((comms || []) as unknown as Array<{ faturamento: number | null; lead_id: string | null; lead: { canal: string | null } | null }>)) {
        if (!passesFunnel(c.lead_id)) continue;
        const canal = c.lead?.canal || '(sem canal)';
        sums[canal] = (sums[canal] || 0) + (Number(c.faturamento) || 0);
      }
      return metas.map(m => {
        const real = sums[m.target_label ?? ''] || 0;
        const meta = Number(m.valor) || 0;
        return {
          meta_id:   m.id,
          key:       m.target_label,
          label:     m.target_label || '',
          funnel_id: m.funnel_id || null,
          meta,
          real,
          percent:   meta > 0 ? (real / meta) : 0,
        };
      });
    }

    return [];
  };
}
